import { allFunds, fundOf, fundSymbols, ISSUERS, type Distribution, type Fund, type Holding, type TaxYear } from './decode';

/**
 * Everything the app reads, over the two shipped files.
 *
 * The same surface the data package's accessors had, so this is a change of
 * where the values come from rather than of what anything asks for. The
 * pipeline keeps its own copies of these — it works against the database, and
 * they are the source the artefact is built from — but nothing in a browser
 * reaches for them now.
 *
 * `bun run validate` compares the two implementations value by value on every
 * build. Two implementations of the same thing is a real cost, and that check
 * is what makes it a safe one.
 */

export type { Fund, Holding, Distribution, TaxYear };
/** The universe entry and the fund profile are one record now, so the name the
    app knows resolves to it. */
export type UniverseEntry = Fund;
export { allFunds, fundOf, fundSymbols, ISSUERS };

const F = (symbol: string) => fundOf(symbol);

// --- profile -----------------------------------------------------------------

export const fund = F;
export const name = (symbol: string): string | null => F(symbol)?.name ?? null;
export const managementFee = (symbol: string): number | null => F(symbol)?.managementFee ?? null;
export const riskRating = (symbol: string): string | null => F(symbol)?.riskRating ?? null;
export const assets = (symbol: string): number | null => F(symbol)?.assets ?? null;
export const distributionFrequency = (symbol: string): string | null => F(symbol)?.distributionFrequency ?? null;
export const mandateOf = (symbol: string): string | null => F(symbol)?.mandate ?? null;
export const tierOf = (symbol: string): string | null => F(symbol)?.tier ?? null;
export const isCovered = (symbol: string): boolean => F(symbol)?.tier === 'covered';
export const issuerOf = (symbol: string): string | null => F(symbol)?.issuer ?? null;

export const COVERED: Fund[] = [];
for (const f of allFunds()) if (f.tier === 'covered') COVERED.push(f);

export const inTier = (...tiers: string[]): Fund[] => allFunds().filter((f) => tiers.includes(f.tier));

// --- collections --------------------------------------------------------------

export const holdingsOf = (symbol: string): Holding[] => F(symbol)?.holdings ?? [];
export const holdings = holdingsOf;
/**
 * Flat, one row per slice, carrying its bucket — the shape the pipeline's
 * accessor returns and the app filters on. The grouped form is below.
 */
export interface Allocation {
    fund: string;
    kind: string;
    name: string;
    weight: number;
}

export const allocationsOf = (symbol: string): Allocation[] => {
    const grouped = F(symbol)?.allocations ?? {};
    return Object.entries(grouped).flatMap(([kind, slices]) => slices.map((s) => ({ fund: symbol, kind, name: s.name, weight: s.weight })));
};

/**
 * Slices grouped by bucket, heaviest first — the same order `holdingsOf` uses,
 * so a breakdown and a holdings list read the same way round.
 *
 * Published order is not kept. Issuers emit these alphabetically as often as by
 * weight, and a reader scanning a sector breakdown wants the concentration at
 * the top, not "Asset Management" because it starts with an A.
 *
 * Sorted on a copy: `slices` is the packed table's own array, and sorting it in
 * place would reorder it for every later caller.
 */
export const allocationsByKind = (symbol: string): { kind: string; slices: { name: string; weight: number }[] }[] =>
    Object.entries(F(symbol)?.allocations ?? {}).map(([kind, slices]) => ({ kind, slices: [...slices].sort((a, b) => b.weight - a.weight) }));

/** Largest slice of the first breakdown published, for a cell with room for one
    fact rather than a pie. */
export const topAllocation = (symbol: string): Allocation | null => {
    const rows = allocationsOf(symbol);
    if (rows.length === 0) return null;
    const kind = rows[0].kind;
    return rows.filter((a) => a.kind === kind).reduce((best, a) => (a.weight > best.weight ? a : best));
};

export const distributions = (symbol: string): Distribution[] => F(symbol)?.distributions ?? [];
export const taxFor = (symbol: string): TaxYear[] => F(symbol)?.tax ?? [];

/** Earliest payment on file, or null when there are none. */
export const distributionsFrom = (symbol: string): string | null => distributions(symbol)[0]?.exDate ?? null;
export const latestTax = (symbol: string): TaxYear | null => taxFor(symbol).at(-1) ?? null;

/** Return of capital as a share of the year's distribution. Not income — it
    reduces cost base — so it is the component worth surfacing on its own. */
export const returnOfCapitalShare = (symbol: string): number | null => {
    const tax = latestTax(symbol);
    if (!tax || !tax.totalPerUnit) return null;
    return (tax.returnOfCapital / tax.totalPerUnit) * 100;
};

// --- prices --------------------------------------------------------------------

export const candles = (symbol: string): { date: string; close: number }[] => {
    const f = F(symbol);
    if (!f) return [];
    const out: { date: string; close: number }[] = new Array(f.closes.length);
    for (let i = 0; i < f.closes.length; i++) out[i] = { date: f.dates[i], close: f.closes[i] };
    return out;
};

export const latestClose = (symbol: string): number | null => {
    const f = F(symbol);
    return f && f.closes.length > 0 ? f.closes[f.closes.length - 1] : null;
};

export const latestCloseDate = (symbol: string): string | null => {
    const f = F(symbol);
    return f && f.dates.length > 0 ? f.dates[f.dates.length - 1] : null;
};

/** Close on a date, or the last one before it — the price as at a day that may
    be a weekend. Null before the history starts. */
export const closeAsOf = (symbol: string, date: string): number | null => {
    const f = F(symbol);
    if (!f) return null;
    let found: number | null = null;
    for (let i = 0; i < f.dates.length; i++) {
        if (f.dates[i] > date) break;
        found = f.closes[i];
    }
    return found;
};

export const price = latestClose;
export const priceAsOf = latestCloseDate;
/** Which of these have a price history. Takes a list so it reads the same as
    the pipeline's version, which had to be told what to consider. */
export const pricedSymbols = (symbols: string[] = fundSymbols()): string[] => symbols.filter((s) => (F(s)?.closes.length ?? 0) > 0);

// --- derived ---------------------------------------------------------------------

const oneYearBefore = (asOf: string): string => {
    const [y, mo, d] = asOf.split('-').map(Number);
    return `${y - 1}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

/**
 * Distributions paid in the twelve months ending at `asOf`.
 *
 * Cash only: a non-cash special raises cost base rather than paying out.
 * Null rather than 0 when the history starts after the window opens — summing
 * what is on file would undercount and still look like a real yield.
 */
export const trailingDistribution = (symbol: string, asOf: string): number | null => {
    const paid = distributions(symbol).filter((d) => d.isCash);
    if (paid.length === 0) return null;
    const from = oneYearBefore(asOf);
    if (paid[0].exDate > from) return null;
    return paid.filter((d) => d.exDate > from && d.exDate <= asOf).reduce((sum, d) => sum + d.amount, 0);
};

/** Prefers the trailing twelve months of actual payments; falls back to the
    published tax total, which is a real figure for a real year, just older. */
export const annualDistribution = (symbol: string, asOf: string): number | null =>
    trailingDistribution(symbol, asOf) ?? latestTax(symbol)?.totalPerUnit ?? null;

/** True when the figure came from the tax table rather than from payments, so
    the UI can say it is a year old rather than current. */
export const distributionIsTrailingTaxYear = (symbol: string, asOf: string): boolean =>
    trailingDistribution(symbol, asOf) == null && latestTax(symbol) != null;

export const instrumentYield = (symbol: string, asOf: string): number | null => {
    const annual = annualDistribution(symbol, asOf);
    const p = price(symbol);
    return annual == null || p == null || p <= 0 ? null : (annual / p) * 100;
};

export const isPriced = (symbol: string, asOf: string): boolean => price(symbol) != null && annualDistribution(symbol, asOf) != null;

export const passesThroughFees = (symbol: string): boolean => F(symbol)?.feesPassThrough === true;

/** A fund by its published name, for the issuers that list fund holdings
    without a ticker. Built once; names come from the same artefact. */
const nameToSymbol = new Map<string, string>();
for (const f of allFunds()) if (f.name != null) nameToSymbol.set(f.name.trim().toLowerCase(), f.symbol);

const symbolByName = (label: string | null): string | null => (label == null ? null : (nameToSymbol.get(label.trim().toLowerCase()) ?? null));

/**
 * What holding this actually costs per year, including the fees of anything it
 * holds.
 *
 * A fund of funds charges nothing directly and passes on the fees of its
 * holdings, so its published 0% is not comparable to a fund running its own
 * book. Null rather than a partial figure if any underlying fee is unknown —
 * a weighted average built from part of a portfolio and shown as the total is
 * exactly the invented number this data exists to avoid.
 *
 * That rule used to be stated and not kept. A holding published without a
 * ticker was skipped rather than treated as unknown, and Harvest publishes its
 * fund-of-fund holdings by name only — so HDIF, which holds ten fee-charging
 * ETFs, weighted none of them and reported 0.00%. Untickered holdings are now
 * matched back by name, and anything that still will not resolve returns null.
 *
 * Weights can exceed 100% where the fund is levered, and that is correct: fees
 * are charged on gross exposure, not on your capital.
 */
export const effectiveManagementFee = (symbol: string): number | null => {
    const own = managementFee(symbol);
    if (own == null) return null;
    if (!passesThroughFees(symbol)) return own;

    let weighted = 0;
    for (const holding of holdingsOf(symbol)) {
        // Only fund holdings carry a fee of their own; a share does not, and
        // neither does cash or a written option.
        if (!/\bETF\b/i.test(holding.name ?? '')) continue;
        const inner = holding.symbol ?? symbolByName(holding.name);
        if (inner == null) return null;
        const underlying = managementFee(inner);
        if (underlying == null) return null;
        weighted += (holding.weight / 100) * underlying;
    }
    return own + weighted;
};
